import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import RestUtilities from '../../services/RestUtilities';

import { blobUrl, artistStatus } from '../../Constants';
import EmailList from '../../components/EditArtist/EmailList';
import CategoryList from '../../components/EditArtist/CategoryList';
import EmailIcon from '@mui/icons-material/Email';

import {
  Avatar,
  MenuItem,
  Grid,
  Typography,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  InputAdornment,
  Button,
  useTheme,
  Container,
  TextFieldProps,
} from '@mui/material';
import { loadingState, toastState } from '../../state/RecoilState';
import { useSetRecoilState } from 'recoil';
import {
  Artist,
  ArtistCategory,
  ArtistEmail,
  ArtistPrice,
} from '../../@types/api';

const sx = {
  grid: {
    flexGrow: 1,
  },
  emailContainer: {
    width: '100%',
    margin: 2,
  },
  formControl: {
    my: 1,
  },
  large: {
    width: 7,
    height: 7,
    mr: 2,
  },
  paper: {
    px: 2,
    mt: 1,
    overflow: 'auto',
  },
  editorWrapper: {
    padding: 1,
  },
  button: {
    margin: 0.5,
  },
};

const newArtist: Artist = {
  artistId: 0,
  artistBiog: '',
  artistSummary: '',
  artistName: '',
  photo: undefined,
  artistPhoto: 0,
  status: 12,
  slug: '',
  artistMedia: [],
  videos: [],
  songs: [],
  youtubeVideos: [],
  artistMediaId: 0,
  artistMediaTypeId: 0,
  mediaTypeId: 0,
  mediaTypeDescription: '',
  mediaType: '',
  uploadId: 0,
  upload: undefined,
  color: '',
  artistOrder: 0,
  isLive: false,
  contactName: '',
  telephoneNumber: '',
  artistFbUrl: '',
  artistInstagramUrl: '',
  token: '',
  artistPrice: undefined,
  emails: undefined,
  categories: [],
};
const EditArtist = ({ isAdding }: { isAdding: boolean }) => {
  const theme = useTheme();
  const { slug } = useParams();
  const setToast = useSetRecoilState(toastState);
  const setLoading = useSetRecoilState(loadingState);
  const [artist, setArtist] = useState<Artist | undefined>(newArtist);
  const [artistChanges, setArtistChanges] = useState({});
  const [email, setEmail] = useState('');
  const displayArtist: Artist | undefined = artist
    ? { ...artist, ...artistChanges }
    : undefined;
  const imageOptions = artist?.artistMedia?.filter((x) => x.mediaTypeId === 1);

  console.log(displayArtist);
  useEffect(() => {
    const getAllData = async () => {
      setLoading(true);
      const res = await RestUtilities.get(`/api/artist/Get/${slug}`);
      if (res.ok) {
        const dbArtist: Artist = res.content;
        console.log(dbArtist);
        setArtist(dbArtist);
      } else {
        setArtist(undefined);
      }
      setToast('Artist Loaded');
      setLoading(false);
    };
    if ((!artist?.artistId || slug !== artist.slug) && !isAdding) {
      getAllData();
    }
  }, [artist, isAdding, setArtist, setLoading, setToast, slug]);

  const handleEmailChange = (event: any) => {
    setEmail(event.target.value);
  };

  const addEmail = () => {
    const emails = displayArtist?.emails ?? [];
    emails.push({ emailAddress: email, isPrimary: false });
    const nextChanges = {
      ...artistChanges,
      emails,
    };
    setArtistChanges(nextChanges);
  };

  const deleteEmail = (emailToDelete: ArtistEmail) => {
    const emails = displayArtist?.emails?.filter(
      (item) => item !== emailToDelete
    );
    const nextChanges = {
      ...artistChanges,
      emails,
    };
    setArtistChanges(nextChanges);
  };

  const addCategory = (categoryId: number) => {
    const categories = displayArtist?.categories
      ? [...displayArtist.categories]
      : [];
    categories.push({ categoryId } as ArtistCategory);
    const nextChanges = {
      ...artistChanges,
      categories,
    };
    setArtistChanges(nextChanges);
  };

  const removeCategory = (categoryId: any) => {
    const categories = displayArtist?.categories.filter(
      (x) => x.categoryId !== categoryId
    );
    const nextChanges = {
      ...artistChanges,
      categories,
    };
    setArtistChanges(nextChanges);
  };

  const togglePrimary = (emailToChange: ArtistEmail) => {
    emailToChange.isPrimary = !emailToChange.isPrimary;
    const emails = displayArtist?.emails;
    const nextChanges = {
      ...artistChanges,
      emails,
    };
    setArtistChanges(nextChanges);
  };

  const changeArtist = ({
    field,
    changes,
  }: {
    field: keyof Artist;
    changes: any;
  }) => {
    const nextChanges = {
      ...artistChanges,
      [field]: changes,
    } as Artist;
    setArtistChanges(nextChanges);
  };

  const priceEditorProps = (field: keyof ArtistPrice, large = false) => {
    const price = displayArtist?.artistPrice
      ? displayArtist?.artistPrice[field]
      : 0;
    return {
      onChange: ({ target: change }: React.ChangeEvent<HTMLInputElement>) => {
        const nextChanges = {
          ...displayArtist,
          artistPrice: { ...displayArtist?.artistPrice },
        };
        nextChanges.artistPrice[field] = Number(change.value);
        setArtistChanges(nextChanges);
      },
      value: price ?? 0,
      label: field[0].toUpperCase() + field.slice(1),
      type: 'number',
      sx: { ...sx.formControl, mr: 1, maxWidth: large ? '150px' : '80px' },
      size: 'small',
    } as TextFieldProps;
  };

  const textEditorProps = (field: keyof Artist) =>
    ({
      onChange: ({ target: change }: React.ChangeEvent<HTMLInputElement>) =>
        changeArtist({
          field: field,
          changes: change.value,
        }),
      value: displayArtist?.[field] || '',
      label: field[0].toUpperCase() + field.slice(1),
      sx: sx.formControl,
      size: 'small',
      fullWidth: true,
    } as TextFieldProps);

  const numericEditorProps = (field: keyof Artist) =>
    ({
      onChange: ({ target: change }: React.ChangeEvent<HTMLInputElement>) =>
        changeArtist({
          field: field,
          changes: change.value,
        }),
      type: 'number',
      value: displayArtist?.[field] ?? 0,
      label: field[0].toUpperCase() + field.slice(1),
      sx: sx.formControl,
      size: 'small',
      fullWidth: true,
    } as TextFieldProps);

  const renderImageMenuItem = (option: any) => {
    if (!option) return null;
    return (
      <MenuItem
        key={option.artistMediaId}
        value={option.uploadId}
        sx={{ py: 0 }}
      >
        <Avatar
          src={`${blobUrl}/images/${option.upload?.fileName}`}
          sx={{
            width: '40px',
            height: '20px',
            mr: 2,
          }}
          variant='square'
        />
        <span>
          {option.uploadId}: {option.upload?.fileName}
        </span>
      </MenuItem>
    );
  };

  const renderSelected = (value: Number) => {
    const option = imageOptions?.find((x) => x.uploadId === value);
    return renderImageMenuItem(option);
  };

  const handleUpdateArtist = async () => {
    setLoading(true);
    const res = await RestUtilities.put(
      `/api/artist/Update/${displayArtist?.artistId}`,
      displayArtist
    );
    if (res.ok) {
      setArtist(res.content);
      setToast('Artist Updated');
    } else {
      setToast('Something went wrong! Artist NOT Updated');
    }
    setLoading(false);
  };

  if (!displayArtist) return null;
  return (
    <Container maxWidth='lg'>
      {(isAdding || !!displayArtist.artistId) && (
        <Box sx={{ pt: 2 }}>
          <Typography variant='h5' color='primary' gutterBottom>
            {displayArtist.artistId > 0
              ? `${displayArtist.artistId}: ${displayArtist.artistName}`
              : 'Add New Artist'}
          </Typography>

          <Grid container spacing={2} sx={sx.grid}>
            {artist?.photo && (
              <Grid item xs={12} md={12} lg={12}>
                <Box
                  component='img'
                  sx={{
                    width: 200,
                  }}
                  alt='artist.artistName'
                  src={`${blobUrl}/images/${artist.photo?.fileName}`}
                />
              </Grid>
            )}
            <Grid item xs={12} md={12} lg={12}>
              <Button
                variant='contained'
                color='primary'
                size='small'
                onClick={handleUpdateArtist}
              >
                {isAdding ? 'Insert Arist' : 'Update Artist'}
              </Button>
            </Grid>
            <Grid item sm={12} md={6} xl={3}>
              <Typography
                variant='subtitle1'
                color={theme.palette.text.secondary}
              >
                Site Details
              </Typography>
              <TextField {...textEditorProps('artistName')} />
              <TextField {...textEditorProps('slug')} />
              <TextField {...numericEditorProps('artistOrder')} />
              <TextField {...textEditorProps('color')} />
              <FormControl fullWidth>
                <Select
                  fullWidth
                  labelId='status-select-label'
                  id='status-simple-select'
                  onChange={(e) =>
                    changeArtist({
                      field: 'status',
                      changes: e.target.value,
                    })
                  }
                  value={displayArtist?.status ?? 12}
                  size='small'
                >
                  <MenuItem value={artistStatus.LIVE}>Live</MenuItem>
                  <MenuItem value={artistStatus.ADMINONLY}>Admin Only</MenuItem>
                  <MenuItem value={artistStatus.REMOVED}>Removed</MenuItem>
                </Select>
              </FormControl>
              {imageOptions && imageOptions.length > 0 && (
                <FormControl sx={sx.formControl} fullWidth size='small'>
                  <InputLabel id='photo-label'>Main Photo</InputLabel>
                  <Select
                    labelId='photo-label'
                    id='photo-select'
                    value={displayArtist.artistPhoto}
                    label='Main Photo'
                    onChange={(event) => {
                      changeArtist({
                        field: 'artistPhoto',
                        changes: event.target.value,
                      });
                    }}
                    size='small'
                    renderValue={renderSelected}
                  >
                    {imageOptions?.map((option) => renderImageMenuItem(option))}
                  </Select>
                </FormControl>
              )}
            </Grid>
            <Grid item sm={12} md={6} xl={3}>
              <Typography
                variant='subtitle1'
                color={theme.palette.text.secondary}
              >
                Band Details
              </Typography>
              <TextField {...textEditorProps('token')} />
              <TextField {...textEditorProps('contactName')} />
              <TextField {...textEditorProps('telephoneNumber')} />
              <TextField {...textEditorProps('artistFbUrl')} />
              <TextField {...textEditorProps('artistInstagramUrl')} />
            </Grid>
            <Grid item sm={12} md={6} xl={3}>
              <Typography
                variant='subtitle1'
                color={theme.palette.text.secondary}
              >
                Categories
              </Typography>
              <CategoryList
                categories={displayArtist.categories}
                addCategory={addCategory}
                removeCategory={removeCategory}
                className={''}
              />
              <Typography
                variant='subtitle1'
                color={theme.palette.text.secondary}
              >
                Prices
              </Typography>
              <TextField {...priceEditorProps('bookingFee')} />
              <TextField {...priceEditorProps('priceA')} />
              <TextField {...priceEditorProps('priceB')} />
              <TextField {...priceEditorProps('priceC')} />
              <TextField {...priceEditorProps('priceD')} />
            </Grid>
            <Grid item sm={12}>
              <Typography
                variant='subtitle1'
                color={theme.palette.text.secondary}
              >
                Prices by Region
              </Typography>
              <TextField {...priceEditorProps('edinburgh', true)} />
              <TextField {...priceEditorProps('glasgow', true)} />
              <TextField {...priceEditorProps('aberdeen', true)} />
              <TextField {...priceEditorProps('aberdeenNorth', true)} />
              <TextField {...priceEditorProps('borders', true)} />
              <TextField {...priceEditorProps('fife', true)} />
              <TextField {...priceEditorProps('stAndrews', true)} />
              <TextField {...priceEditorProps('dundee', true)} />
              <TextField {...priceEditorProps('dumfries', true)} />
              <TextField {...priceEditorProps('highlandsIslands', true)} />
              <TextField {...priceEditorProps('inverness', true)} />
              <TextField {...priceEditorProps('westLothian', true)} />
              <TextField {...priceEditorProps('ayrshire', true)} />
              <TextField {...priceEditorProps('lochLomondTrossachs', true)} />
              <TextField {...priceEditorProps('perthshire', true)} />
            </Grid>
            <Grid item sm={12} md={6} xl={3}>
              <Typography
                variant='subtitle1'
                color={theme.palette.text.secondary}
              >
                Emails
              </Typography>
              <Typography color='primary' variant='body2'>
                Tick primary email
              </Typography>
              <EmailList
                emails={displayArtist?.emails ?? []}
                deleteEmail={deleteEmail}
                togglePrimary={togglePrimary}
              />
              <Box
                display={'flex'}
                alignItems={'center'}
                justifyContent={'space-between'}
              >
                <FormControl
                  sx={{ ...sx.formControl, width: '100%' }}
                  variant='outlined'
                  size='small'
                >
                  <InputLabel htmlFor='add-email'>Add a new email</InputLabel>
                  <OutlinedInput
                    id='add-email'
                    value={email}
                    label='Add a new email'
                    onChange={handleEmailChange}
                    startAdornment={
                      <InputAdornment position='start'>
                        <EmailIcon />
                      </InputAdornment>
                    }
                  />
                </FormControl>
                <Button
                  variant='outlined'
                  color='secondary'
                  size='small'
                  onClick={addEmail}
                  sx={{ minWidth: '60px', ml: 1 }}
                >
                  Add
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}
    </Container>
  );
};
export default EditArtist;
